import React from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { motion } from "framer-motion";
import { useReducedMotion } from "@/hooks/use-reduced-motion";

/**
 * Hero-Sektion der Website
 * Zeigt den Haupttitel und Call-to-Action für den Veranstaltungsraum
 * Verwendet ein Hintergrundbild mit Overlay und animierte Elemente
 */
export default function HeroSection() {
  const prefersReducedMotion = useReducedMotion();
  
  /**
   * Scrollt zur Buchungssektion
   * Wird sowohl vom Button als auch vom Scroll-Indikator aufgerufen
   */
  const scrollToBooking = () => {
    document.getElementById("booking-section")?.scrollIntoView({ 
      behavior: prefersReducedMotion ? "auto" : "smooth" 
    });
  };

  return (
    <section className="relative h-screen flex items-center justify-center overflow-hidden bg-[#0D5C63]">
      {/* Hintergrundbild mit Overlay - Preload optimiert */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(13, 92, 99, 0.85), rgba(13, 92, 99, 0.9)), url('/images/hero.avif')`
        }}
      />
      
      {/* Hauptinhalt - LCP optimiert */}
      <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
        {/* LCP-Element ohne Animation für schnellste Darstellung */}
        <div className="mb-6">
          <h1 className="text-5xl md:text-7xl font-light leading-tight">
            Veranstaltungsraum<br />
            <span className="font-semibold text-yellow-500">Arenarisquelle</span>
          </h1>
        </div>
        
        {/* Restliche Inhalte mit Animation */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.8, ease: "easeOut" }}
        >
          
          {/* Untertitel */}
          <p className="text-xl md:text-2xl font-light mb-8 text-white">
            Ihr perfekter Raum für besondere Momente in Bad Tabarz
          </p>
          
          {/* Call-to-Action Button */}
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.8, delay: 0.5 }}
          >
            <Button 
              onClick={scrollToBooking}
              size="lg"
              className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-semibold px-12 py-4 text-lg rounded-full shadow-2xl hover:shadow-yellow-500/25 transform hover:scale-105 transition-all duration-300"
            >
              Jetzt anfragen
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll-Indikator */}
      <motion.button 
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white cursor-pointer bg-white/10 hover:bg-white/20 rounded-full p-3 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-[#0D5C63]"
        onClick={scrollToBooking}
        animate={prefersReducedMotion ? false : { y: [0, 10, 0] }}
        transition={prefersReducedMotion ? { duration: 0 } : { duration: 2, repeat: Infinity }}
        aria-label="Nach unten zur Buchung scrollen"
        type="button"
      >
        <ChevronDown className="w-8 h-8" />
      </motion.button>
    </section>
  );
}